import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  SelectOption,
  Stack,
  Card,
  Typography,
  Link,
  useCoralBreakpoints,
} from '@krakentech/coral';
import { Formik } from 'formik';

import {
  FormContainer,
  FormSubmitButton,
  MutedText,
  Select,
  PageError,
  H2Skeleton,
  TextFieldSkeleton,
  Body2Skeleton,
  FullWidthButtonSkeleton,
  FormComponent,
} from '@/components';
import RegistrationStatusAlert from '@/components/auth/RegistrationStatusAlert';
import { useGuestApplyForMeterContext } from '@/context/GuestApplyForMeterContext';
import {
  USER_REGISTRATION_JOURNEY_KEY_OPTIONS,
  useUserRegistrationContext,
} from '@/context/UserRegistrationContext';
import { useAccountIdentification } from '@/hooks/auth/useAccountIdentification';
import { useGuestOneTimeAccess } from '@/hooks/auth/useGuestOneTimeAccess';
import { useUserRegistrationStatus } from '@/hooks/auth/useUserRegistrationStatus';
import { useVerifyIdentity } from '@/hooks/auth/useVerifyIdentity';
import { useVerifyIdentityFallback } from '@/hooks/auth/useVerifyIdentityFallback';
import { useSyncFeatureFlag } from '@/hooks/utils/useFeatureFlags';
import { FeatureNames } from '@/types/features';
import { GuestJourneyEnum } from '@/types/generated/graphql/schema';
import {
  fullNameToObject,
  generateUserNameSelectOptions,
} from '@/utils/formatters/name';
import {
  getUserIdFromBlackholeUserObject,
  userRegistrationJourneyConfig,
} from '@/utils/identifyAndVerify';
import { INTERNAL_PATHS } from '@/utils/urls';

const UserPicker = ({
  journeyKey,
}: {
  journeyKey: USER_REGISTRATION_JOURNEY_KEY_OPTIONS;
}) => {
  const { push } = useRouter();
  const isRegisterV2Enabled = useSyncFeatureFlag(
    FeatureNames.RegisterForOnlineAccountV2
  );
  const {
    mutate: mutateVerifyIdentity,
    isError: isVerifyIdentityError,
    isSuccess: isVerifyIdentitySuccess,
  } = useVerifyIdentity();
  const {
    mutate: mutateVerifyIdentityFallback,
    isError: isVerifyIdentityFallbackError,
    isSuccess: isVerifyIdentityFallbackSuccess,
  } = useVerifyIdentityFallback();

  const {
    accountNumber,
    postcode,
    selectedAccountUserName,
    setSelectedAccountUserName,
    setHasMobileNumber,
    setHasDateOfBirth,
    blackholeAccountUsers,
    setSelectedUserId,
    surname,
    scopedToken,
    billingAddress,
  } = useUserRegistrationContext();

  const { setEmail } = useGuestApplyForMeterContext();

  const isGuestApplyForMeterJourney =
    journeyKey === USER_REGISTRATION_JOURNEY_KEY_OPTIONS.GUEST_APPLY_FOR_METER;
  const {
    data: accountIdentificationData,
    refetch: refetchAccountIdentification,
    isError: isAccountIdentificationError,
    isSuccess: isAccountIdentificationSuccess,
  } = useAccountIdentification({
    scopedToken,
    familyName: surname,
  });
  const {
    data: userRegistrationStatusData,
    refetch: refetchUserRegistrationStatus,
    isError: isUserRegistrationStatusError,
  } = useUserRegistrationStatus({
    scopedToken:
      (userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails ||
        isGuestApplyForMeterJourney) &&
      isAccountIdentificationSuccess
        ? scopedToken
        : '',
  });

  const {
    mutate: mutateGuestOneTimeAccess,
    isSuccess: isGuestOneTimeAccessSuccess,
    isError: isGuestOneTimeAccessError,
  } = useGuestOneTimeAccess({
    scopedToken,
  });

  const shouldRedirectToFirstStep = blackholeAccountUsers.length <= 1;

  const notEnoughInfoToProceed =
    accountIdentificationData?.blackholeEmailAccountUser &&
    !accountIdentificationData.blackholeEmailAccountUser.hasMobileNumber &&
    !accountIdentificationData.blackholeEmailAccountUser.hasDateOfBirth;

  // Tracks the previous scoped token to detect changes and prevent unnecessary refetches
  const previousScopedTokenRef = useRef(scopedToken);
  // Prevents multiple initial verifications when the component mounts or re-renders
  const hasInitializedRef = useRef(false);

  // Handle initial state and verify selected user
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      selectedAccountUserName.givenName &&
      blackholeAccountUsers.length > 0
    ) {
      hasInitializedRef.current = true;
      const userId = getUserIdFromBlackholeUserObject(
        blackholeAccountUsers,
        selectedAccountUserName
      );

      // If we have a preselected user, verify their identity to get a new scoped token
      if (userId && selectedAccountUserName.givenName) {
        const commonInputs = {
          postcode,
          familyName: selectedAccountUserName.familyName,
          accountUserId: userId,
        };

        if (
          (isRegisterV2Enabled &&
            journeyKey ===
              USER_REGISTRATION_JOURNEY_KEY_OPTIONS.REGISTER_FOR_ONLINE_ACCOUNT) ||
          isGuestApplyForMeterJourney
        ) {
          // Extract first line of address from the billing address value
          const firstLineOfAddress = String(billingAddress.value)
            .split(',')[0]
            .trim();

          mutateVerifyIdentityFallback({
            input: {
              ...commonInputs,
              firstLineOfAddress,
            },
          });
        } else {
          mutateVerifyIdentity({
            input: {
              ...commonInputs,
              accountNumber,
            },
          });
        }
      }
    }
  }, [
    selectedAccountUserName,
    blackholeAccountUsers,
    postcode,
    billingAddress,
    accountNumber,
    isRegisterV2Enabled,
    journeyKey,
    isGuestApplyForMeterJourney,
    mutateVerifyIdentity,
    mutateVerifyIdentityFallback,
  ]);

  // Watch for verify identity success and update the scoped token
  useEffect(() => {
    if (isVerifyIdentitySuccess || isVerifyIdentityFallbackSuccess) {
      // Force a refetch of account identification with the new token
      refetchAccountIdentification();
      if (
        userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails ||
        isGuestApplyForMeterJourney
      ) {
        refetchUserRegistrationStatus();
      }
    }
  }, [
    isVerifyIdentitySuccess,
    isVerifyIdentityFallbackSuccess,
    refetchAccountIdentification,
    refetchUserRegistrationStatus,
    journeyKey,
    isGuestApplyForMeterJourney,
  ]);

  // Handle scoped token changes
  useEffect(() => {
    if (scopedToken && scopedToken !== previousScopedTokenRef.current) {
      previousScopedTokenRef.current = scopedToken;
      refetchAccountIdentification();

      if (
        userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails ||
        isGuestApplyForMeterJourney
      ) {
        refetchUserRegistrationStatus();
      }
    }
  }, [
    scopedToken,
    refetchAccountIdentification,
    refetchUserRegistrationStatus,
    journeyKey,
    isGuestApplyForMeterJourney,
  ]);

  const handleUserNameChange = (selectedUserName?: SelectOption | null) => {
    if (
      !blackholeAccountUsers.length ||
      !selectedUserName?.value ||
      !blackholeAccountUsers[0].givenName
    ) {
      return;
    }

    const currentSelectedFullName = fullNameToObject(
      selectedUserName?.value as string
    );

    // Using the selected user's given name, find the user's ID
    const userId = getUserIdFromBlackholeUserObject(
      blackholeAccountUsers,
      currentSelectedFullName
    );

    if (
      selectedAccountUserName.givenName !== currentSelectedFullName.givenName
    ) {
      const commonInputs = {
        postcode,
        familyName: currentSelectedFullName.familyName,
        accountUserId: userId,
      };

      if (
        (isRegisterV2Enabled &&
          journeyKey ===
            USER_REGISTRATION_JOURNEY_KEY_OPTIONS.REGISTER_FOR_ONLINE_ACCOUNT) ||
        isGuestApplyForMeterJourney
      ) {
        const firstLineOfAddress = String(billingAddress.value)
          .split(',')[0]
          .trim();

        mutateVerifyIdentityFallback({
          input: {
            ...commonInputs,
            firstLineOfAddress,
          },
        });
      } else {
        mutateVerifyIdentity({
          input: {
            ...commonInputs,
            accountNumber,
          },
        });
      }

      if (isGuestApplyForMeterJourney) {
        setEmail('');
      }

      setSelectedAccountUserName(currentSelectedFullName);
      setSelectedUserId(userId);
    }
  };

  const handleSubmit = ({
    userName: selectedUserName,
  }: {
    userName: { value: string; label: string };
  }) => {
    setHasMobileNumber(
      accountIdentificationData?.blackholeEmailAccountUser?.hasMobileNumber
    );
    setHasDateOfBirth(
      accountIdentificationData?.blackholeEmailAccountUser?.hasDateOfBirth
    );

    const currentSelectedFullName = fullNameToObject(
      selectedUserName?.value as string
    );
    // Using the selected user's given name, find the user's ID. We'll ideally want to just have the dropdown values as an index and use that instead. But this is fine for a hotfix so fuck it innit.
    const userId = getUserIdFromBlackholeUserObject(
      blackholeAccountUsers,
      currentSelectedFullName
    );

    setSelectedAccountUserName(currentSelectedFullName);
    setSelectedUserId(userId);

    const userEmail = userRegistrationStatusData?.email;

    // If we are on the guest apply for meter journey, we check if the user has an email in kraken, if so we can store the email in context and call the mutation to send the apply for meter email link and redirect to the success page
    if (isGuestApplyForMeterJourney && userEmail) {
      mutateGuestOneTimeAccess({
        input: {
          journey: GuestJourneyEnum.ApplyForMeter,
        },
      });
      setEmail(userEmail);
    } else {
      if (!isVerifyIdentityError) {
        // This redirects to the third step in the journey
        push(INTERNAL_PATHS[`${journeyKey}_SECURITY`].path);
      }
    }
  };

  // If there's only one user, they don't need to be on this page so they've got here sneakily or in error. So redirect them to the first step. This logic also navigates the user back to the first step if they've navigated back to this page without filling in the previous page
  useEffect(() => {
    if (shouldRedirectToFirstStep) {
      push(INTERNAL_PATHS[`${journeyKey}_YOUR_ACCOUNT`].path);
    }
  }, [shouldRedirectToFirstStep, push, journeyKey]);

  // Set default user ID on mount and call verifyIdentityFallback
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      blackholeAccountUsers.length > 0 &&
      isRegisterV2Enabled &&
      journeyKey ===
        USER_REGISTRATION_JOURNEY_KEY_OPTIONS.REGISTER_FOR_ONLINE_ACCOUNT &&
      !selectedAccountUserName.givenName // Only set default if no user is selected
    ) {
      hasInitializedRef.current = true;
      const defaultUser = blackholeAccountUsers[0];
      const defaultFullName = {
        givenName: defaultUser.givenName,
        familyName: defaultUser.familyName,
      };
      const defaultUserId = getUserIdFromBlackholeUserObject(
        blackholeAccountUsers,
        defaultFullName
      );
      setSelectedUserId(defaultUserId);
      setSelectedAccountUserName(defaultFullName);

      const firstLineOfAddress = String(billingAddress.value)
        .split(',')[0]
        .trim();

      mutateVerifyIdentityFallback({
        input: {
          postcode,
          familyName: defaultFullName.familyName,
          accountUserId: defaultUserId,
          firstLineOfAddress,
        },
      });
    }
  }, [
    blackholeAccountUsers,
    isRegisterV2Enabled,
    journeyKey,
    billingAddress,
    postcode,
    setSelectedUserId,
    setSelectedAccountUserName,
    mutateVerifyIdentityFallback,
    selectedAccountUserName.givenName,
  ]);

  // This is only for guest apply for meter journey
  useEffect(() => {
    if (isGuestOneTimeAccessSuccess) {
      push(INTERNAL_PATHS[`${journeyKey}_SUCCESS`].path);
    }
  }, [isGuestOneTimeAccessSuccess, push, journeyKey]);

  if (shouldRedirectToFirstStep) {
    return <LoadingState />;
  }

  if (isAccountIdentificationError || isUserRegistrationStatusError) {
    return <PageError />;
  }

  const selectOptions = generateUserNameSelectOptions(
    blackholeAccountUsers || []
  );

  const genericError =
    (!userRegistrationJourneyConfig[journeyKey]
      .showDetailedNotEnoughInfoAlert &&
      notEnoughInfoToProceed) ||
    (isRegisterV2Enabled || isGuestApplyForMeterJourney
      ? isVerifyIdentityFallbackError
      : isVerifyIdentityError) ||
    isGuestOneTimeAccessError;

  return (
    <Formik
      key={`${selectedAccountUserName.givenName}-${selectedAccountUserName.familyName}`}
      initialValues={{
        userName: {
          label: `${selectedAccountUserName.givenName} ${selectedAccountUserName.familyName}`,
          value: `${selectedAccountUserName.givenName}_${selectedAccountUserName.familyName}`,
        },
      }}
      onSubmit={handleSubmit}
    >
      <FormComponent>
        <FormContainer>
          <Stack gap="lg" direction="vertical">
            <Card>
              <Stack direction="vertical" gap="smMd">
                <Typography variant="h2">Who are you?</Typography>
                <Select
                  onChange={handleUserNameChange}
                  name="userName"
                  id="userName"
                  label="Select your name"
                  values={selectOptions}
                />
                <MutedText>
                  If you can&apos;t see your name listed,{' '}
                  <Link
                    target="_blank"
                    href={process.env.NEXT_PUBLIC_HELP_LINK}
                  >
                    get in touch
                  </Link>
                </MutedText>
              </Stack>
            </Card>

            <RegistrationStatusAlert
              accountIdentificationData={accountIdentificationData}
              userRegistrationStatus={userRegistrationStatusData}
              journeyKey={journeyKey}
              notEnoughInfoToProceed={notEnoughInfoToProceed}
              genericError={genericError}
            />

            <FormSubmitButton
              disabled={
                notEnoughInfoToProceed ||
                (userRegistrationJourneyConfig[journeyKey]
                  .blockForRegisteredEmails &&
                  !!userRegistrationStatusData?.email)
              }
            >
              Continue
            </FormSubmitButton>
          </Stack>
        </FormContainer>
      </FormComponent>
    </Formik>
  );
};

const LoadingState = () => {
  const { isMinMd } = useCoralBreakpoints();

  return (
    <FormContainer>
      <Stack gap="lg" direction="vertical">
        <Card>
          <Stack direction="vertical" gap="smMd">
            <H2Skeleton width={isMinMd ? 300 : undefined} />
            <TextFieldSkeleton width={isMinMd ? 600 : undefined} />
            <Body2Skeleton width={isMinMd ? 200 : undefined} />
          </Stack>
        </Card>

        <FullWidthButtonSkeleton />
      </Stack>
    </FormContainer>
  );
};

export default UserPicker;
